import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  catchError,
  filter,
  finalize,
  shareReplay,
  take,
  tap,
  throwError,
} from 'rxjs';
import { AuthRequest } from '../models/api/authRequest';
import { Token } from '../models/api/token';
import { UserBoResetPasswordForm } from '../models/api/userBoResetPasswordForm';
import { AuthenticationApiService } from './api/authentication.api.service';
import { Logger } from './logger.service';
import { TokenStoreService } from './stores/token-store.service';

const log = new Logger('AuthenticationService');

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private readonly router = inject(Router);
  private readonly authenticationApiService = inject(AuthenticationApiService);
  private readonly tokenStoreService = inject(TokenStoreService);

  /**
   * Mutex de refresh : quand plusieurs requêtes tombent en 401 simultanément,
   * un seul appel `/auth/refresh` est émis ; les autres s'abonnent au même
   * stream et récupèrent le nouveau token.
   */
  private refresh$: Observable<Token> | null = null;
  private readonly accessToken$ = new BehaviorSubject<string | null>(null);

  clearSession() {
    log.debug('clear session');
    this.tokenStoreService.clear();
    this.refresh$ = null;
    this.accessToken$.next(null);
    this.router.navigate(['/']);
  }

  login(payload: AuthRequest) {
    log.debug('login');
    return this.authenticationApiService.login(payload).pipe(
      tap((token) => {
        if (!token.isTmpPassword) {
          this.tokenStoreService.setToken(token);
        }
      })
    );
  }

  resetPassword(payload: UserBoResetPasswordForm) {
    log.debug('reset password');
    return this.authenticationApiService.login(payload).pipe(
      tap((token) => {
        if (!token.isTmpPassword) {
          this.tokenStoreService.setToken(token);
        }
      })
    );
  }

  logout() {
    log.debug('logout');
    const refreshToken = this.tokenStoreService.$refreshToken();
    return this.authenticationApiService.logout(refreshToken).pipe(
      catchError(() => EMPTY),
      tap(() => {
        this.clearSession();
      })
    );
  }

  /**
   * Renvoie un Observable du nouveau Token. Partagé entre tous les appelants
   * en cas de 401 concurrents (mutex via `shareReplay` + champ d'instance).
   * Si pas de refreshToken disponible ou si l'appel échoue, on déclenche
   * `clearSession` et on propage l'erreur.
   */
  refresh(): Observable<Token> {
    if (this.refresh$) {
      return this.refresh$;
    }

    const refreshToken = this.tokenStoreService.$refreshToken();
    if (!refreshToken) {
      log.debug('refresh impossible : pas de refresh token en store');
      this.clearSession();
      return throwError(() => new Error('Missing refresh token'));
    }

    log.debug('refresh token');
    this.refresh$ = this.authenticationApiService.refresh(refreshToken).pipe(
      tap((token) => {
        this.tokenStoreService.setToken(token);
        this.accessToken$.next(token.accessToken ?? null);
      }),
      catchError((err) => {
        this.clearSession();
        return throwError(() => err);
      }),
      finalize(() => {
        this.refresh$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.refresh$;
  }

  /**
   * Stream du dernier accessToken obtenu après refresh — utile pour les
   * requêtes 401 mises en attente : elles attendent un nouveau token non
   * nul puis retentent.
   */
  onAccessTokenRefreshed(): Observable<string> {
    return this.accessToken$.pipe(
      filter((t): t is string => !!t),
      take(1)
    );
  }

  introspectToken(token: string) {
    log.debug('introspect token');
    return this.authenticationApiService.introspection(token);
  }
}
